import { Injectable, inject } from '@angular/core';
import { BaseQueryRepository } from '@okcargo-apps/shared-infrastructure';
import {
  CollaboratorQuestionairResponse,
  CollaboratorRoutesPreferencesResponse,
  DocumentMetadataResponse,
  FindCollaboratorQuestionairQuery,
  FindCollaboratorQuestionairQueryHandler,
  FindFreightForCarrierQuery,
  FindFreightForCarrierQueryHandler,
  FreightBasicForCarrierResponse,
  FreightDetailForCarrierResponse,
  SearchCollaboratorRoutesPreferencesQuery,
  SearchCollaboratorRoutesPreferencesQueryHandler,
  SearchDocumentMetadataQuery,
  SearchDocumentMetadataQueryHandler,
  SearchFinishedFreightsForCarriersQuery,
  SearchFinishedFreightsForCarriersQueryHandler,
  SearchFreightsWithPendingDocumentationForCarriersQuery,
  SearchFreightsWithPendingDocumentationForCarriersQueryHandler,
  SearchInProgressFreightsForCarriersQuery,
  SearchInProgressFreightsForCarriersQueryHandler,
} from '@okcargo/query-processor';
import { Observable, from } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FreightQueryService {
  private readonly queryRepository = inject(BaseQueryRepository);

  private readonly searchInProgressFreightsForCarriersQueryHandler: SearchInProgressFreightsForCarriersQueryHandler;
  private readonly searchNotInProgressFreightsForCarriersQueryHandler: SearchFinishedFreightsForCarriersQueryHandler;
  private readonly findFreightForCarrierQueryHandler: FindFreightForCarrierQueryHandler;
  private readonly searchDocumentMetadataQueryHandler: SearchDocumentMetadataQueryHandler;
  private readonly searchFreightsForCarriersWithPendingDocumentationQueryHandler: SearchFreightsWithPendingDocumentationForCarriersQueryHandler;

  //-- user/company preferences
  private readonly findCollaboratorQuestionairQueryHandler: FindCollaboratorQuestionairQueryHandler;
  private readonly searchCollaboratorRoutesPreferencesQueryHandler: SearchCollaboratorRoutesPreferencesQueryHandler;

  constructor() {
    const queryRepository = this.queryRepository;

    this.searchInProgressFreightsForCarriersQueryHandler = new SearchInProgressFreightsForCarriersQueryHandler(
      queryRepository,
    );
    this.searchNotInProgressFreightsForCarriersQueryHandler = new SearchFinishedFreightsForCarriersQueryHandler(
      queryRepository,
    );
    this.findFreightForCarrierQueryHandler = new FindFreightForCarrierQueryHandler(queryRepository);
    this.searchDocumentMetadataQueryHandler = new SearchDocumentMetadataQueryHandler(queryRepository);
    this.searchFreightsForCarriersWithPendingDocumentationQueryHandler =
      new SearchFreightsWithPendingDocumentationForCarriersQueryHandler(queryRepository);

    //-- user/company preferences
    this.findCollaboratorQuestionairQueryHandler = new FindCollaboratorQuestionairQueryHandler(queryRepository);
    this.searchCollaboratorRoutesPreferencesQueryHandler = new SearchCollaboratorRoutesPreferencesQueryHandler(
      queryRepository,
    );
  }

  // TODO manage metadata in subsequent release

  public searchInProgressFreightsForCarriers(
    query: SearchInProgressFreightsForCarriersQuery,
  ): Promise<FreightBasicForCarrierResponse[]> {
    return this.searchInProgressFreightsForCarriersQueryHandler
      .handle(query)
      .then((response) => response.items as unknown as FreightBasicForCarrierResponse[]);
  }

  public searchFinishedFreightsForCarriers(
    query: SearchFinishedFreightsForCarriersQuery,
  ): Promise<FreightBasicForCarrierResponse[]> {
    return this.searchNotInProgressFreightsForCarriersQueryHandler.handle(query).then((response) => response.items);
  }

  public searchFreightsForCarriersWithPendingDocumentation(
    query: SearchFreightsWithPendingDocumentationForCarriersQuery,
  ): Promise<FreightBasicForCarrierResponse[]> {
    return this.searchFreightsForCarriersWithPendingDocumentationQueryHandler
      .handle(query)
      .then((response) => response.items);
  }

  public findFreight(query: FindFreightForCarrierQuery): Observable<FreightDetailForCarrierResponse> {
    return from(this.findFreightForCarrierQueryHandler.handle(query));
  }

  public searchDocumentMetadata(query: SearchDocumentMetadataQuery): Promise<DocumentMetadataResponse[]> {
    return this.searchDocumentMetadataQueryHandler.handle(query);
  }

  public findCollaboratorQuestionair(
    query: FindCollaboratorQuestionairQuery,
  ): Promise<CollaboratorQuestionairResponse> {
    return this.findCollaboratorQuestionairQueryHandler.handle(query);
  }

  //-- user/company preferences
  searchCollaboratorRoutesPreferences(
    query: SearchCollaboratorRoutesPreferencesQuery,
  ): Promise<CollaboratorRoutesPreferencesResponse[]> {
    return this.searchCollaboratorRoutesPreferencesQueryHandler.handle(query);
  }
}
